'use client';

import { Button, Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@shared/components/shadcn';
import { HorizontalSlider } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useIsMobile } from '@shared/utils';
import type { ComponentProps } from 'react';

type FilterButtonProps = {
  title: string;
  children: React.ReactNode;
  className?: string;
  appliedFiltersCount?: number;
  disabled?: boolean;
  onOpen?: () => void;
};

/**
 * FiltersContainer component for the FilterButton. It wraps the content of the sheet component that contains the filters.
 * Extends HTMLDivElement props.
 * @returns The FiltersContainer HTMLDivElement component
 */
function FiltersContainer({ className, ...props }: ComponentProps<'div'>) {
  return <div className={twMerge('pb-6 px-6 flex flex-col gap-8 mb-auto', className)} data-testid="filters-container" {...props} />;
}

/**
 * ActionsContainer component for the FilterButton. It wraps action buttons of the sheet component.
 * Extends HTMLDivElement props.
 * @returns The ActionsContainer HTMLDivElement component
 */
function ActionsContainer({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={twMerge('sticky bottom-0 right-0 left-0 w-full flex justify-between items-center gap-6 p-5 shadow-top-m bg-container-base rounded-t-lg', className)}
      data-track-container="Actions"
      data-testid="actions-container"
      {...props}
    />
  );
}

/**
 * ClearButton component for the FilterButton. It has styling and tracking attributes, but expects onClick to handle clear filters logic.
 * Extends Button props.
 * @returns The ClearButton Button component
 */
function ClearButton({ className, children, ...props }: ComponentProps<typeof Button>) {
  return (
    <Button className={twMerge('basis-1/2', className)} variant="secondary" size="m" data-track-name="Clear" data-testid="clear-button" {...props}>
      {children}
    </Button>
  );
}

/**
 * ShowButton component for the FilterButton. It has styling and tracking attributes, but expects onClick to handle applying filters logic.
 * It uses SheetClose asChild to close the sheet when the button is clicked.
 * Extends Button props.
 * @returns The ShowButton Button component
 */
function ShowButton({ className, children, ...props }: ComponentProps<typeof Button>) {
  return (
    <SheetClose asChild>
      <Button className={twMerge('basis-1/2', className)} variant="primary" size="m" data-track-name="Show" data-testid="show-button" {...props}>
        {children}
      </Button>
    </SheetClose>
  );
}

function FilterButton({ title, appliedFiltersCount, className, children, onOpen, disabled = false }: FilterButtonProps) {
  const isMobile = useIsMobile();

  return (
    <Sheet>
      <SheetTrigger asChild className={className} onClick={onOpen} data-testid="filter-button">
        <Button
          variant="secondary"
          size="s"
          aria-label={`${title}${appliedFiltersCount ? ` (${appliedFiltersCount} filters applied)` : ''}`}
          data-track-game-filters={appliedFiltersCount}
          data-track-name="Filters"
          disabled={disabled}
        >
          <HorizontalSlider size="extra-small" />
          {appliedFiltersCount && appliedFiltersCount > 0 ? <span className="btn-label-s">({appliedFiltersCount})</span> : null}
        </Button>
      </SheetTrigger>
      <SheetContent className={isMobile ? 'max-h-[calc(100vh-64px)]' : ''} side={isMobile ? 'bottom' : 'right'}>
        <SheetHeader>
          <SheetTitle className="heading-5">{title}</SheetTitle>
          <SheetDescription className="sr-only">{title}</SheetDescription>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

FilterButton.FiltersContainer = FiltersContainer;
FilterButton.ActionsContainer = ActionsContainer;
FilterButton.ClearButton = ClearButton;
FilterButton.ShowButton = ShowButton;

export { FilterButton };
